<script>
	// libraries
	import { LayerCake, ScaledSvg, Html } from 'layercake'
	import { scaleBand, scaleTime } from 'd3-scale'

	// components
	import AxisX from './AxisX.svelte'
	import AxisY from './AxisY.svelte'
	import Columns from './Columns.svelte'
	import Mark from './Mark.svelte'
	import Labels from './Labels.svelte'
	import SharedTooltip from './SharedTooltip.percent-range.html.svelte'

	// other
	import {
		getXTicksMonths,
		getXTicksLabelsPositionsYearsMiddle,
		getXTicksLabelsYearsMiddle,
		getXTicksYears
	} from '../utils'

	// ///////////////////////////////////////////////////////////
	// properties
	// ///////////////////////////////////////////////////////////
	export let data
	export let color = '#000'
	export let marks = null
	export let axis = true
	export let centerAxis = false
	export let invert = false
	export let key
	export let yMax = null
	export let yTicks

	export let tooltipDateFct = null
	export let tooltipValueFct = null
	export let tooltipWidth

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	const xKey = 'start_date'
	const yKey = key

	// ///////////////////////////////////////////////////////////
	// axes
	// ///////////////////////////////////////////////////////////

	const xMin = data.at(0).start_date
	const xMax = data.at(-1).end_date

	const xTicks = getXTicksMonths(xMin, xMax)
	const xTicksYears = getXTicksYears(xMin, xMax)
	const xTicksLabelsPositions = getXTicksLabelsPositionsYearsMiddle(xMin, xMax)
	const xTicksLabels = getXTicksLabelsYearsMiddle(xMin, xMax)

	// ///////////////////////////////////////////////////////////
	// hover
	// ///////////////////////////////////////////////////////////

	export let hovered
</script>

<div class="chart-container" class:axis={!!axis}>
	<LayerCake
		ssr={true}
		percentRange={true}
		position="absolute"
		padding={{ top: 0, right: 0, bottom: 0, left: 0 }}
		x={xKey}
		y={yKey}
		xScale={scaleBand().paddingInner([0.128]).round(false)}
		yDomain={[0, yMax]}
		yRange={invert ? [0, 100] : [100, 0]}
		{data}>
		<Html>
			<AxisY gridlines={true} ticks={yTicks} xTick={10} />
		</Html>

		<ScaledSvg>
			<Columns
				fill={color}
				highlight={(d, i) => i === data.length - 1}
				{hovered} />
		</ScaledSvg>
		<Html>
			{#if marks}
				{#each marks as m}
					<Mark x={m.x} y1={m.y1} y2={m.y2} label={m.label} />
				{/each}
			{/if}

			{#if !hovered}
				<Labels {color} which={(d, i) => i === data.length - 1} {invert} />
			{/if}
			<SharedTooltip {invert} bind:d={hovered} w={tooltipWidth || 150} let:d>
				<div class="tooltip">
					<div class="tooltip-date">{@html tooltipDateFct(d)}</div>
					<div class="tooltip-value" style="color: {color}">
						{@html tooltipValueFct(d)}
					</div>
				</div>
			</SharedTooltip>
		</Html>
	</LayerCake>
</div>
{#if axis}
	<div class="axis-container">
		<LayerCake
			ssr={true}
			percentRange={true}
			position="absolute"
			padding={{ top: 0, right: 0, bottom: 0, left: 0 }}
			xScale={scaleTime()}
			xDomain={[xMin, xMax]}>
			<Html>
				<AxisX
					gridlines={false}
					offset={2}
					yTick={0}
					baselineColor="#888"
					baseline={true}
					ticks={xTicks}
					tickMarks={true}
					noLabels={true}
					tickHeight={5} />
				<AxisX
					offset={2}
					gridlines={false}
					yTick={0}
					baseline={false}
					ticks={xTicksYears}
					tickMarks={true}
					noLabels={true}
					tickHeight={14} />

				<AxisX
					offset={2}
					gridlines={false}
					yTick={8}
					baseline={false}
					ticksAtBaseline={true}
					ticks={xTicksLabelsPositions}
					allTicksCentered={true}
					formatTick={(d, i) => xTicksLabels[i]} />
			</Html>
		</LayerCake>
	</div>
{/if}

{#if centerAxis}
	<div class="axis-container">
		<LayerCake
			ssr={true}
			percentRange={true}
			position="absolute"
			padding={{ top: 0, right: 0, bottom: 0, left: 0 }}
			xScale={scaleTime()}
			xDomain={[xMin, xMax]}>
			<Html>
				<AxisX
					gridlines={false}
					offset={7}
					yTick={0}
					baselineColor="#888"
					baseline={true}
					ticks={[]} />
				<AxisX
					gridlines={false}
					offset={5}
					yTick={0}
					baseline={false}
					ticks={xTicks}
					tickMarks={true}
					noLabels={true}
					tickHeight={5} />

				<AxisX
					offset={2}
					gridlines={false}
					yTick={0}
					baseline={false}
					ticks={xTicksYears}
					tickMarks={true}
					noLabels={true}
					tickHeight={11} />
			</Html>
		</LayerCake>
	</div>
{/if}

<style lang="scss">
	@import '../main.scss';

	.chart-container {
		width: 100%;
		height: 100%;
		position: relative;
	}
	.chart-container.axis {
		height: calc(100% - 20px);
	}

	.axis-container {
		width: 100%;
		height: 20px;
		position: relative;
	}

	.tooltip {
		padding: 7px 4px;
		text-align: center;
	}

	.tooltip-date {
		font-size: 12px;
		line-height: 14px;
		color: #9b9b9b;

		b {
			font-weight: 500;
		}
	}

	.tooltip-value {
		font-size: 14px;
		line-height: 15px;
		font-weight: 500;
		margin-top: 1px;
	}
</style>
